declare const __COMMIT_HASH__: string;

import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { useNextStep } from "nextstepjs";
import { useAtomValue, useSetAtom } from "jotai";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { cn } from "@/lib/utils";
import { HomeBottomNav } from "@/components/HomeBottomNav";
import { useDailyTrophyBonusUnclaimed } from "@/components/DailyTrophyBonusButton";
import { useSquadListInfinite, useStats } from "@/lib/hooks/useApi";
import { teleportDialogOpenAtom } from "@/lib/homeUiAtoms";
import { isErrorPageAtom } from "@/lib/atoms";

const NAVBAR_ROUTES = [
  "/home",
  "/terminal",
  "/missions",
  "/changelog",
  "/shop",
  "/leaderboard",
];

export const Route = createFileRoute("/(game)")({
  component: AppLayout,
});

function AppLayout() {
  const { isMobile } = useUserAgent();
  const { user, login } = usePrivy();
  const { currentTour, currentStep, setCurrentStep, closeNextStep } =
    useNextStep();
  const location = useLocation();
  const navigate = useNavigate();
  const setTeleportDialogOpen = useSetAtom(teleportDialogOpenAtom);

  const walletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(walletAddress ?? "");
  const userId = statsData?.userId;
  const { data: squadData } = useSquadListInfinite(userId, { limit: 10 });
  const squadCount = squadData?.pages.flatMap((p) => p.squads).length ?? 0;

  const isErrorPage = useAtomValue(isErrorPageAtom);
  const showNavbar = !isErrorPage && NAVBAR_ROUTES.includes(location.pathname);
  const leaderboardBonusUnclaimed = useDailyTrophyBonusUnclaimed("leaderboard");
  const terminalBonusUnclaimed = useDailyTrophyBonusUnclaimed("terminal");

  return (
    <div
      className={cn(
        "relative h-[100svh] text-white w-full max-w-md mx-auto flex flex-col gap-2.5 overflow-x-hidden",
        isMobile && "p-0",
        !isMobile && "pt-2 pb-2 px-2",
      )}
    >
      <div
        id="page-content"
        className={cn(
          "relative",
          "flex-1 mx-auto w-full flex flex-col",
          "overflow-x-hidden",
          isMobile && "",
          !isMobile &&
            "rounded-xl ring-[3px] border-b-[3px] ring-black border-black",
        )}
      >
        <div
          className={cn(
            "relative",
            "flex flex-1 flex-col w-full overflow-hidden",
          )}
          style={{ viewTransitionName: "page-outlet" }}
        >
          <Outlet />
          <p className="absolute bottom-0 right-2 opacity-15 z-999 pointer-events-none">
            v{__COMMIT_HASH__}
          </p>
        </div>

        {showNavbar && (
          <HomeBottomNav
            items={[
              {
                iconName: "terminal",
                label: "Terminal",
                active: location.pathname === "/terminal",
                dot: !!user && terminalBonusUnclaimed,
                onClick: () => {
                  navigate({ to: "/terminal" });
                  // setTeleportDialogOpen(true);
                  // if (currentTour === "lobbyWelcomeTour" && currentStep === 1) {
                  //   setTimeout(() => setCurrentStep(2), 300);
                  // }
                },
                // dataStep: "lobby-modes-nav",
              },
              {
                iconName: "leaderboard",
                label: "Leaderboard",
                active: location.pathname === "/leaderboard",
                dot: !!user && leaderboardBonusUnclaimed,
                onClick: () => navigate({ to: "/leaderboard" }),
              },
              {
                iconName: "home",
                label: "Lobby",
                center: true,
                active: location.pathname === "/home",
                onClick: () => navigate({ to: "/home" }),
              },
              {
                iconName: "shop",
                label: "Shop",
                active: location.pathname === "/shop",
                onClick: () => {
                  if (!user) {
                    login();
                    return;
                  }
                  navigate({ to: "/shop" });
                },
              },
              {
                iconName: "missions",
                label: "Missions",
                active: location.pathname === "/missions",
                onClick: () => {
                  if (!user) {
                    login();
                    return;
                  }
                  navigate({ to: "/missions" });
                },
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
